import { QueryClient, queryOptions, useQuery } from "@tanstack/react-query";
import { UserProfileResponse } from "./users.types";

import { shouldSignOutOnError } from "@platform/shared/errors";
import { fetchMFAPreference, signOut } from "aws-amplify/auth";
import api from "~/shared/api/core/api";
import { Params } from "~/shared/api/types/params";
import { posthogClient } from "~/shared/lib/posthog/posthog-client";

export const USERS_KEYS = {
  all: ["user"] as const,
  profile: () => [...USERS_KEYS.all, "profile"] as const,
  detail: (id: string) => [...USERS_KEYS.all, id] as const,
  demographics: (id: string) => [...USERS_KEYS.detail(id), "demographics"] as const,
  authPreferences: () => [...USERS_KEYS.all, "authPreferences"] as const,
  surveys: (id: string) => [...USERS_KEYS.detail(id), "surveys"] as const,
  surveysList: (id: string, params: Params) => [...USERS_KEYS.surveys(id), params] as const,
  survey: (userId: string, assignedId: string) => [...USERS_KEYS.surveys(userId), "detail", assignedId] as const,
};

export const USERS_QUERIES = {
  profile: () => {
    return queryOptions({
      queryKey: USERS_KEYS.profile(),
      queryFn: async ({ signal }) => {
        try {
          const response = await api.get<UserProfileResponse>("/auth/profile", {
            signal,
          });

          posthogClient?.identify(response.data.id.toString(), {
            email: response.data.email,
            username: response.data.username,
            user_type: response.data.type,
            programs: response.data.profile?.programs?.map((program) => program.id) ?? [],
          });
          posthogClient?.setPersonPropertiesForFlags({
            id: response.data.id.toString(),
            email: response.data.email,
            username: response.data.username,
            user_type: response.data.type,
            programs: response.data.profile?.programs?.map((program) => program.id) ?? [],
          });

          return response.data;
        } catch (e) {
          if (shouldSignOutOnError(e)) {
            await signOut();
          }
          throw e;
        }
      },
      retry: 1,
      staleTime: 60 * 1000 * 10, // 10min
    });
  },
  userAuthPreferences: () => {
    return queryOptions({
      queryKey: USERS_KEYS.authPreferences(),
      queryFn: async () => {
        const preferences = await fetchMFAPreference();
        return preferences;
      },
    });
  },
  demographics: (id: string) => {
    return queryOptions({
      queryKey: USERS_KEYS.demographics(id),
      queryFn: async ({ signal }) => {
        const response = await api.get(`/users/${id}/demographics`, {
          signal,
        });

        return response.data;
      },
    });
  },
  surveys: (id: string, params: Params) => {
    return queryOptions({
      queryKey: USERS_KEYS.surveysList(id, params),
      queryFn: async ({ signal }) => {
        const response = await api.get(`/users/${id}/assigned-surveys`, {
          ...params,
          signal,
        });

        return response.data;
      },
    });
  },
  survey: (userId: string, assignedId: string) => {
    return queryOptions({
      queryKey: USERS_KEYS.survey(userId, assignedId),
      queryFn: async ({ signal }) => {
        const response = await api.get(`/users/${userId}/assigned-surveys/${assignedId}`, {
          signal,
        });

        return response.data;
      },
    });
  },
};

export function useUserProfile() {
  return useQuery({
    ...USERS_QUERIES.profile(),
    throwOnError: false,
    retry: false,
  });
}

export async function getUserType(queryClient: QueryClient) {
  const user = await queryClient.ensureQueryData(USERS_QUERIES.profile());

  return user.type;
}
